import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { Recipe, RecipeCategory, RecipeLanguageFramework, RelatedRecipesResponse } from '@zerops/models/recipes';
import { getNormalizedHttpParams } from '@zerops/zef/core';
import { QueryParams } from '@zerops/zef/entities';
import { environment } from '@zerops/zerops/env';
import { StaticContent, DocLink } from '../recipe-base/recipe-base.model';

@Injectable({ providedIn: 'root' })
export class StrapiDataService {
  // # Deps
  #httpClient = inject(HttpClient);
  #apiEndpoint = `${environment.webApiUrl}/api`;

  recipes$(queryParams?: QueryParams | string) {
    return this.#httpClient.get<{ data: Recipe[]; meta: any; }>(
      `${this.#apiEndpoint}/recipes`,
      {
        params: getNormalizedHttpParams(queryParams),
        headers: { zefSkipAuthHeader: 'true' }
      }
    );
  }

  categories$(queryParams?: QueryParams | string) {
    return this.#httpClient.get<{ data: RecipeCategory[]; meta: any; }>(
      `${this.#apiEndpoint}/recipe-categories`,
      {
        params: getNormalizedHttpParams(queryParams),
        headers: { zefSkipAuthHeader: 'true' }
      }
    );
  }

  languagesFrameworks$(queryParams?: QueryParams | string) {
    return this.#httpClient.get<{ data: RecipeLanguageFramework[]; meta: any; }>(
      `${this.#apiEndpoint}/recipe-language-frameworks`,
      {
        params: getNormalizedHttpParams(queryParams),
        headers: { zefSkipAuthHeader: 'true' }
      }
    );
  }

  relatedRecipes$(recipeId: string | number, limit = 4) {
    return this.#httpClient.get<RelatedRecipesResponse>(
      `${this.#apiEndpoint}/recipes/${recipeId}/related`,
      {
        params: { limit: limit.toString() },
        headers: { zefSkipAuthHeader: 'true' }
      }
    );
  }

  staticContent$() {
    // Strapi returns data already in Record format
    interface StrapiStaticContent {
      environmentGuides: Record<string, string>;
      serviceTypeDocs: Record<string, DocLink[]>;
      platformDocs: DocLink[] | null;
    }

    return this.#httpClient.get<{ data: StrapiStaticContent }>(
      `${this.#apiEndpoint}/static-content`,
      {
        headers: { zefSkipAuthHeader: 'true' }
      }
    ).pipe(
      map((response) => this.#transformStaticContent(response.data))
    );
  }

  #transformStaticContent(raw: {
    environmentGuides: Record<string, string>;
    serviceTypeDocs: Record<string, DocLink[]>;
    platformDocs: DocLink[] | null;
  } | null | undefined): StaticContent {
    // Defensive null check for malformed API responses
    if (!raw) {
      return {
        environmentGuides: {},
        serviceTypeDocs: {},
        platformDocs: null
      };
    }

    return {
      environmentGuides: raw.environmentGuides || {},
      serviceTypeDocs: raw.serviceTypeDocs || {},
      platformDocs: raw.platformDocs || null
    };
  }
}
